// Attachment dropzone (Story #4613, Stream 8a) — drag/drop + browse + paste,
// with the three distinct cap states the design calls out:
//   • per-ticket 5/5 cap → greyed, "remove one to add another" (removable cap)
//   • second video       → transient "Only one video per ticket"
//   • lifetime cap        → its OWN copy ("Start a new ticket…"), NEVER "remove one"
// Paste is scoped: it no-ops when focus is in a text field (Subject/Description),
// so pasting text there never hijacks a screenshot into the attachments.

import { useCallback, useEffect, useRef, useState } from 'react'
import { ACCEPT_ATTR, MAX_FILES, isVideoType } from '../../constants/support'

interface AttachmentDropzoneProps {
  onFiles: (files: File[]) => void
  fileCount: number
  hasVideo: boolean
  lifetimeLocked?: boolean
  /** compact padding variant for the reply composer */
  compact?: boolean
}

const CAP_COPY = 'You’ve attached the maximum of 5 files. Remove one to add another.'
const LIFETIME_COPY =
  'You’ve reached the attachment limit for this ticket. Start a new ticket to send more files.'
const ONE_VIDEO_COPY = 'Only one video per ticket.'

export default function AttachmentDropzone({
  onFiles,
  fileCount,
  hasVideo,
  lifetimeLocked = false,
  compact = false,
}: AttachmentDropzoneProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [dragOver, setDragOver] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)

  const capReached = fileCount >= MAX_FILES
  const disabled = lifetimeLocked || capReached

  const handle = useCallback(
    (files: File[]) => {
      if (files.length === 0) return
      if (lifetimeLocked) {
        setNotice(LIFETIME_COPY)
        return
      }
      // Surface the one-video rule when a second video is attempted.
      const incomingVideos = files.filter((f) => isVideoType(f.type)).length
      if (incomingVideos > 0 && (hasVideo || incomingVideos > 1)) {
        setNotice(ONE_VIDEO_COPY)
      } else if (fileCount >= MAX_FILES) {
        setNotice(CAP_COPY)
      } else {
        setNotice(null)
      }
      onFiles(files)
    },
    [fileCount, hasVideo, lifetimeLocked, onFiles],
  )

  // Scoped paste: only when focus is NOT in an input/textarea (so pasting text
  // into Subject/Description is never diverted into attachments).
  useEffect(() => {
    function onPaste(e: ClipboardEvent) {
      const el = document.activeElement as HTMLElement | null
      if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable)) return
      const items = e.clipboardData?.items
      if (!items) return
      const files: File[] = []
      for (const item of Array.from(items)) {
        if (item.kind === 'file') {
          const f = item.getAsFile()
          if (f) files.push(f)
        }
      }
      if (files.length) {
        e.preventDefault()
        handle(files)
      }
    }
    document.addEventListener('paste', onPaste)
    return () => document.removeEventListener('paste', onPaste)
  }, [handle])

  if (disabled) {
    return (
      <div
        className="rounded-xl border-2 border-dashed border-slate-200 bg-slate-50 px-4 py-4 text-center text-[13px] text-slate-500"
        role="status"
      >
        {lifetimeLocked ? LIFETIME_COPY : CAP_COPY}
      </div>
    )
  }

  return (
    <div>
      <div
        role="button"
        tabIndex={0}
        onClick={() => inputRef.current?.click()}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            inputRef.current?.click()
          }
        }}
        onDragOver={(e) => {
          e.preventDefault()
          setDragOver(true)
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault()
          setDragOver(false)
          handle(Array.from(e.dataTransfer.files))
        }}
        className={`cursor-pointer rounded-xl border-2 border-dashed text-center text-[13px] transition-colors ${
          compact ? 'px-4 py-3' : 'px-4 py-5'
        } ${dragOver ? 'border-iflow-400 bg-iflow-100' : 'border-iflow-300 bg-iflow-50'} text-iflow-800`}
      >
        Drag &amp; drop, <span className="font-semibold text-iflow-600">browse</span>, or paste a screenshot (Ctrl/Cmd-V)
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={ACCEPT_ATTR}
          className="hidden"
          onChange={(e) => {
            handle(Array.from(e.target.files ?? []))
            e.target.value = ''
          }}
        />
      </div>
      {notice && <p className="mt-1.5 text-[11.5px] font-medium text-amber-700">{notice}</p>}
    </div>
  )
}
